/* ============================================================================
   LIFECYCLE — FOUR AXES, NEVER ONE FUNNEL          (SITE-RULES 11a)

   The Phase 1 audit drew Lead → Contacted → Trial → Member → Active → At Risk →
   Renewing → Churned → Win-back as a single left-to-right flow with a count under each.
   That shape is wrong and must not be built. Those are FOUR independent axes, and a
   person occupies one value on each of them at the same moment: a Member can be Active
   AND At Risk AND Renewing simultaneously. Drawn as a funnel the counts cannot
   reconcile, and the arrows assert a sequence that does not exist.

     lifecycle  where they are in the relationship  enquiry | trial | member | former
     stage      position INSIDE that lifecycle      new, contacted, prospect_1, …
     status     the commercial state                active | suspended | cancelled
     risk       DERIVED, never stored               riskModel(), or suppressed

   RISK IS NOT A STAGE. It comes from riskModel() and nowhere else (rule 3b2), and Care
   Track / Hold outranks it: a suppressed member has no risk at all (rule 1).

   `lifecycleOf(rec)` is the one answer and every screen reads it, so nothing has to
   re-derive "is this person at risk" from a different rule.
   ============================================================================ */
import { SCORE_RED } from './types.js';
import { anchorsAt } from './anchors.js';
import { riskModel } from './risk.js';
import { daysAgoISO, daysSince, daysUntil, todayISO } from './time.js';
import { cancellationOpen } from './cancellation.js';
export const LIFECYCLE = {
    enquiry: { key: 'enquiry', label: 'Enquiry' },
    trial: { key: 'trial', label: 'Trial' },
    member: { key: 'member', label: 'Member' },
    former: { key: 'former', label: 'Former' },
};
/**
 * Stage lives INSIDE a lifecycle. The lead stages are the seed's own keys, so the
 * pipeline board, the lead record and the console all resolve the same string.
 */
export const STAGES = {
    new: { lifecycle: 'enquiry', label: 'New', order: 1 },
    contacted: { lifecycle: 'enquiry', label: 'Contacted', order: 2 },
    prospect_1: { lifecycle: 'trial', label: 'Trial 1', order: 3 },
    prospect_2: { lifecycle: 'trial', label: 'Trial 2', order: 4 },
    nurture: { lifecycle: 'enquiry', label: 'Nurture', order: 5 },
    won: { lifecycle: 'member', label: 'Joined', order: 6 },
    lost: { lifecycle: 'former', label: 'Did not join', order: 7 },
    onboarding: { lifecycle: 'member', label: 'First 90 days', order: 8 },
    established: { lifecycle: 'member', label: 'Established', order: 9 },
    lapsed: { lifecycle: 'former', label: 'Left', order: 10 },
};
/** The first-90-days window. Day 90 is when a baseline exists and risk starts. */
export const ONBOARDING_DAYS = 90;
/* HOW LONG BEFORE A HOLD ENDS SOMEBODY SHOULD BE ASKED ABOUT IT.
 *
 * Anuj, 2026-09-15: "a day or two before the suspension we should be prompted to ask
 * them (hows the injury, how was vacation, etc, based on the notes)".
 *
 * Two days, and the point is that it is BEFORE. A member whose hold lapsed yesterday
 * gets a reactivation; a member asked on Thursday how the knee is gets a conversation,
 * and the difference between those two is most of what this product is. */
export const HOLD_PROMPT_DAYS = 2;
/**
 * Commercial state, normalised off whatever spelling the source system used. Mindbody
 * and the Salesforce migration between them say frozen / terminated / expired /
 * declined; the product knows three states.
 */
export function statusOf(rec) {
    const s = String(rec?.status ?? '').toLowerCase();
    if (s === 'active')
        return { key: 'active', label: 'Active' };
    if (s === 'suspended' || s === 'frozen')
        return { key: 'suspended', label: 'Suspended' };
    if (s === 'terminated' || s === 'expired' || s === 'declined' || s === 'cancelled') {
        return { key: 'cancelled', label: 'Cancelled' };
    }
    return { key: 'active', label: 'Active' };
}
/**
 * FORMER means the membership ENDED, and it is not the same as "not Active": a
 * suspended member is on Care Track or Hold, still on the roster and deliberately not
 * chased. Only this set is history, and only history is safe to leave unloaded.
 */
export function isFormerMember(m) {
    return statusOf(m).key === 'cancelled';
}
/** A lead carries a `stage`; the seed also prefixes their ids with 'l'. */
export function isLead(rec) {
    const r = rec;
    return !!(r && (r.stage !== undefined || String(r.id ?? '')[0] === 'l'));
}
/** Inside the first 90 days: run the fixed track, do NOT score them (SITE-RULES 3b). */
export function isOnboarding(m, today = todayISO()) {
    const d = daysSince(m?.joinedAt, today);
    return d != null && d <= ONBOARDING_DAYS;
}
/**
 * The one call every screen should make. All four axes plus their labels.
 *
 * Note what is NOT done here, on purpose: a first-90-days member still gets a risk
 * value, exactly as util.js left it, and the consoles and the record are what apply
 * rule 3b by reading `stage === 'onboarding'` (or `isOnboarding()`). Changing that here
 * would have been a silent behaviour change in a translation pass.
 */
export function lifecycleOf(rec, opts = {}) {
    if (!rec)
        return null;
    const today = opts.today ?? todayISO();
    const lead = isLead(rec);
    let stageKey;
    let status;
    let risk = null;
    if (lead) {
        stageKey = (rec.stage ?? 'new');
        status = { key: 'open', label: 'Open' };
        if (stageKey === 'won')
            status = { key: 'converted', label: 'Joined' };
        if (stageKey === 'lost')
            status = { key: 'closed', label: 'Did not join' };
    }
    else {
        const m = rec;
        const st = statusOf(m);
        status = st;
        const joined = daysSince(m.joinedAt, today);
        stageKey = st.key === 'cancelled' ? 'lapsed'
            : (joined != null && joined <= ONBOARDING_DAYS) ? 'onboarding'
                : 'established';
        /* CANCELLATION INITIATED WINS OVER EVERYTHING, suppression included (PRD 4).
           They are frequently Green when they ask, so the score is the last thing that
           should decide where they appear. It carries a null score for the same reason
           suppression does: a surface that forgets to check has no number to print.
           Above suppression on purpose - a member on Care Track who asks about
           cancelling is still the most urgent person in the building, and both states
           stop every automated touch, so nothing is lost by letting this one own it. */
        if (cancellationOpen(m)) {
            risk = {
                key: 'cancelling',
                label: 'Cancellation initiated',
                score: null,
                suppressed: true, // never chased, never automated at
                cancelling: true,
            };
        }
        else if (m.suppression) {
            /* Suppression WINS over the score. Rule 1: a member on Care Track or Hold is
               never scored, never ranked, never automated at — and is still visible, because
               away is not gone. The view says which kind it is and carries a null score, so a
               surface that forgets to check `suppressed` still has no number to print. */
            risk = {
                key: 'suppressed',
                label: m.suppression.kind === 'hold' ? 'On hold' : 'Care Track',
                score: null,
                suppressed: true,
            };
        }
        else if (st.key === 'active') {
            const r = riskModel(m, { today, ...(opts.model ? { model: opts.model } : {}) });
            risk = {
                key: r.tier.toLowerCase(),
                label: r.tier,
                score: r.score,
                why: r.why,
                guidance: r.guidance,
                suppressed: false,
            };
        }
    }
    const st = STAGES[stageKey] ?? {
        lifecycle: (lead ? 'enquiry' : 'member'),
        label: String(stageKey),
        order: 99,
    };
    return {
        lifecycle: st.lifecycle,
        lifecycleLabel: LIFECYCLE[st.lifecycle]?.label ?? st.lifecycle,
        stage: stageKey,
        stageLabel: st.label,
        stageOrder: st.order,
        status: status.key,
        statusLabel: status.label,
        risk,
        isLead: lead,
    };
}
/* ============================================================================
   THE BOARD, AS IT WAS ON ANY GIVEN DAY

   Every predicate below takes an `asOf` and answers from the record's OWN dated
   evidence: a lead's stage transitions, a member's join and cancel dates, their visit
   history, their renewal date. Run it for today and again for a week ago and the
   difference is arithmetic, not decoration.

   Two inputs cannot be rewound and both are stated rather than hidden: a member's
   HEALTH is a stored number with no history on the record, and SUSPENSION is not dated
   either, so both read at today's value in both passes. Attendance and anchors — the
   two signals that actually move — rewind properly.
   ============================================================================ */
export function leadStageAt(l, asOf) {
    const hist = l?.stageHistory;
    if (!Array.isArray(hist) || !hist.length) {
        // No recorded walk: they exist from creation and have only ever been where they are.
        if (!l?.createdAt || l.createdAt > asOf)
            return null;
        return l.stage ?? 'new';
    }
    let cur = null;
    for (const h of hist)
        if (h.at <= asOf)
            cur = h.stage; // history is oldest-first
    return cur; // null = the enquiry did not exist yet
}
export function memberExistsAt(m, asOf) {
    return !!m.joinedAt && m.joinedAt <= asOf;
}
export function memberActiveAt(m, asOf) {
    return memberExistsAt(m, asOf) && (!m.cancelledAt || m.cancelledAt > asOf);
}
export function suppressedAt(m, asOf) {
    const at = m.suppression?.declaredAt;
    return !!(at && at <= asOf);
}
/** Their last visit as at `asOf`, from the visit HISTORY rather than the stored field. */
export function lastVisitAt(m, asOf) {
    let best = null;
    for (const v of m.visits ?? []) {
        if (v.date <= asOf && (!best || v.date > best))
            best = v.date;
    }
    return best;
}
export { anchorsAt };
/**
 * riskModel, run against the member as they WERE.
 *
 * Measured at 3,000 members on a 4x-throttled CPU, this function was 72% of the whole
 * rewind, and almost all of it was recomputing `visits30d` — two Date objects per visit,
 * 70,000 a pass, for a field the scorer never reads. The scorer takes lastVisit, anchors
 * and health and nothing else, so the property is gone rather than made faster.
 *
 * NOT memoised, deliberately. A cache keyed on the member and the date looks free and is
 * not: the member is mutable, so recording an anchor and re-rendering the same day would
 * serve the score from before it existed, and the anchor UI's whole point is that the
 * score moves when you record one.
 */
export function riskAt(m, asOf, opts = {}) {
    return riskModel({ ...m, lastVisit: lastVisitAt(m, asOf), anchors: anchorsAt(m, asOf) }, { today: asOf, ...(opts.model ? { model: opts.model } : {}) });
}
/** Renewal window for the "Renewing" cell. */
export const RENEWAL_WINDOW_DAYS = 30;
/** Win-back reaches people lost recently enough to still be reachable. */
export const WINBACK_WINDOW_DAYS = 90;
/**
 * THE PEOPLE ARE THE SOURCE; THE COUNT IS THEIR LENGTH.
 *
 * There were two hand-written copies of these nine predicates: one counting, and
 * `journeyCellPeople()` in home-sales.js listing. The comment there even said they must
 * mirror each other exactly "or the modal would show a different set of people from the
 * number that was clicked". They drifted anyway: Active counted 261 and opened 255,
 * because one asked "joined and not cancelled" and the other asked "status is Active",
 * and the six members on Hold fall between those. A count that disagrees with its own
 * list is worse than no count.
 *
 * So there is ONE function, it returns PEOPLE, and the count is `.length`. The two
 * cannot drift again because there is nothing left to keep in step.
 */
export function lifecycleGroupsAt(db, asOf, opts = {}) {
    const members = db.members ?? [];
    const leads = db.leads ?? [];
    const stageIs = (l, ...ks) => {
        const s = leadStageAt(l, asOf);
        return s != null && ks.includes(s);
    };
    /* On the roster at that date AND holding a live membership. Suspension is not dated
       on the record, so like health it reads at today's value; joining and cancelling are
       dated and rewind properly. */
    const active = members.filter(m => memberActiveAt(m, asOf) && statusOf(m).key === 'active');
    const scored = active.filter(m => !suppressedAt(m, asOf)); // rule 1, at that date too
    return {
        lead: leads.filter(l => stageIs(l, 'new')),
        contacted: leads.filter(l => stageIs(l, 'contacted')),
        trial: leads.filter(l => stageIs(l, 'prospect_1', 'prospect_2')),
        member: members.filter(m => memberExistsAt(m, asOf)),
        active,
        risk: scored.filter(m => riskAt(m, asOf, opts).score <= SCORE_RED),
        renewing: active.filter(m => {
            const d = daysUntil(m.membershipExpires, asOf);
            return d != null && d >= 0 && d <= RENEWAL_WINDOW_DAYS;
        }),
        churned: members.filter(m => !!m.cancelledAt && m.cancelledAt <= asOf)
            .concat(leads.filter(l => stageIs(l, 'lost'))),
        winback: members.filter(m => {
            if (!m.cancelledAt || m.cancelledAt > asOf)
                return false;
            const d = daysSince(m.cancelledAt, asOf);
            return d != null && d <= WINBACK_WINDOW_DAYS;
        }),
    };
}
/** The people behind one cell. Clicking a count must open exactly the people it counted. */
export function lifecycleCellPeople(db, key, asOf = todayISO(), opts = {}) {
    return lifecycleGroupsAt(db, asOf, opts)[key] ?? [];
}
export function lifecycleCountsAt(db, asOf, opts = {}) {
    const g = lifecycleGroupsAt(db, asOf, opts);
    const out = {};
    for (const k of Object.keys(g))
        out[k] = g[k].length;
    return out;
}
export const LIFECYCLE_CELLS = [
    { key: 'lead', label: 'Lead', subject: 'leads' },
    { key: 'contacted', label: 'Contacted', subject: 'leads' },
    { key: 'trial', label: 'Trial', subject: 'leads' },
    { key: 'member', label: 'Member', subject: 'members' },
    { key: 'active', label: 'Active', subject: 'members' },
    { key: 'risk', label: 'At Risk', tone: 'risk', good: 'down', subject: 'members' },
    { key: 'renewing', label: 'Renewing', subject: 'members' },
    // Cancelled members AND lost leads: the label says "Churned / Lost" for that reason.
    { key: 'churned', label: 'Churned / Lost', tone: 'risk', good: 'down', subject: 'mixed' },
    /* Win-back counts members lost in the last 90 days: the people still close enough to
       reach. It is a pool of recent losses, so it shrinking is the good news, same as risk
       and churn. Up is not better here. */
    { key: 'winback', label: 'Win-back', good: 'down', subject: 'members' },
];
/**
 * SEVEN DAYS, not thirty, and the seed is the reason. The lead board only goes back 48
 * days and the median member's visit history is 31, so rewinding a month empties four of
 * the nine cells and strips the attendance the risk cell is computed from: at 30 days the
 * board reported "new" four times and a -70% swing in risk that was an artifact of
 * missing visits, not a change in anybody's behaviour. A week is the longest window every
 * cell can actually answer, and it is the cadence a floor team works to anyway. Widen it
 * when the data carries a deeper history.
 */
export const LIFECYCLE_WINDOW = 7;
/**
 * Counts today, and the change against the same board `windowDays` ago.
 *
 * A percentage against a tiny base is noise dressed as precision: 1 renewal becoming 12
 * is not "1100%", it is nine more people. Under ten, the change is reported as a count.
 */
export function lifecycleCounts(db, windowDays = LIFECYCLE_WINDOW, opts = {}) {
    const today = opts.today ?? todayISO();
    const w = windowDays || LIFECYCLE_WINDOW;
    const modelOpt = opts.model ? { model: opts.model } : {};
    const now = lifecycleCountsAt(db, today, modelOpt);
    const then = lifecycleCountsAt(db, daysAgoISO(w, today), modelOpt);
    return LIFECYCLE_CELLS.map(c => {
        const n = now[c.key];
        const was = then[c.key];
        const diff = n - was;
        const asPct = was >= 10;
        const cell = {
            key: c.key,
            label: c.label,
            n,
            was,
            windowDays: w,
            diff,
            delta: asPct ? Math.round((diff / was) * 100) : null,
            unit: asPct ? '%' : '',
            quiet: was === 0 && n === 0,
        };
        if (c.tone)
            cell.tone = c.tone;
        if (c.good)
            cell.good = c.good;
        return cell;
    });
}
